import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, normalizeClass as _normalizeClass, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, normalizeStyle as _normalizeStyle, createElementVNode as _createElementVNode, resolveDirective as _resolveDirective, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = { class: "pointer-events-auto" }
const _hoisted_2 = { key: 1 }
const _hoisted_3 = { key: 0 }

import { ref, reactive, onUnmounted, onUpdated, computed } from 'vue'
import LakeShape from '@/assets/svg/LakeShape.vue'
import LakePath from './LakePath.vue'
import SiteStopButton from '../general/SiteStopButton.vue'
import SectionPanel from '../general/SectionPanel.vue'
import { useEventBus } from '@vueuse/core'
import mapData from '@/assets/data/mjp/overhead-map-data.js'
import useLanguage from '@/composables/useLanguage'
import useUserIsOnTour from '@/composables/mjp/useUserIsOnTour'
import useTabIndex from '@/composables/mjp/useTabIndex'
import useSectionToTourIndex from '@/composables/mjp/useSectionToTourIndex'
import OverheadMapVue from '@/components/magicjohnsonpark/OverheadMap.vue'
import { MJPSection, OverheadMapState } from './types'
import { gsap } from 'gsap'
import useSectionIndex from '@/composables/mjp/useSectionIndex'

interface Props {
  currentSection: MJPSection
  currentSectionHasLoaded: boolean
  currentPage: number
  settingsAreOpen: boolean
}


export default /*#__PURE__*/_defineComponent({
  __name: 'OverheadMap',
  props: {
    currentSection: null,
    currentSectionHasLoaded: { type: Boolean },
    currentPage: null,
    settingsAreOpen: { type: Boolean }
  },
  setup(__props: any) {

const props = __props as {
  currentSection: MJPSection
  currentSectionHasLoaded: boolean
  currentPage: number
  settingsAreOpen: boolean
}



const element = ref(null)
const mapSize = {
  x: 725,
  y: 1200,
  margin: 156,
  rightInsetX: 0,
  topInsetY: 214,
}
const mobileBreakpoint = 1024
const sectionChangedBus = useEventBus('section-changed')
const mapText = useLanguage('overheadMap')
const interfaceText = useLanguage('interface')
let resizeTimer = 0
const state = reactive({
  activeSection: MJPSection.None,
  currentState: OverheadMapState.Hidden,
  scaleAmt: 1,
  stopIndex: 0,
  animationTimer: 0,
  animationHasPlayed: false,
})

const updateMapScale = () => {
  if (element.value) {
    if (window.innerWidth < mobileBreakpoint) {
      if (
        window.innerWidth / mapSize.x <
        (window.innerHeight * 0.5) / mapSize.y
      ) {
        state.scaleAmt = window.innerWidth / mapSize.x // map is wider
      } else {
        state.scaleAmt = (window.innerHeight * 0.5) / mapSize.y // window is wider
      }
    } else {
      if (window.innerWidth / mapSize.x < window.innerHeight / mapSize.y) {
        state.scaleAmt = window.innerWidth / mapSize.x // map is wider
      } else {
        state.scaleAmt = window.innerHeight / mapSize.y // window is wider
      }
    }
  }
  return state.scaleAmt
}

const getTranslationFromScaleForState = (scaleAmt, currentState) => {
  let result = {
    x: 0,
    y: 0,
  }
  switch (currentState) {
    case OverheadMapState.Large:
      if (window.innerWidth < mobileBreakpoint) {
        result.y = (window.innerHeight - mapSize.y * scaleAmt) * 0.3
      } else {
        result.y = (window.innerHeight - mapSize.y * scaleAmt) * 0.5
      }
      result.x = (window.innerWidth - mapSize.x * scaleAmt) * 0.5
      break
    case OverheadMapState.Small:
      result.x =
        window.innerWidth -
        (mapSize.x - mapSize.rightInsetX + mapSize.margin) * scaleAmt
      result.y = (mapSize.margin - mapSize.topInsetY) * scaleAmt
      break
  }
  return result
}

const currentMapState = computed(() => {
  let newState: OverheadMapState = state.currentState
  switch (props.currentSection) {
    case MJPSection.Title:
      newState = OverheadMapState.Hidden
      break
    case MJPSection.InvitationToExplore:
      if (props.currentPage < 1) {
        newState = OverheadMapState.Hidden
      } else {
        newState = OverheadMapState.Large
      }
      break
    default:
      if (useUserIsOnTour(props.currentSection)) {
        newState = OverheadMapState.Small
      } else {
        newState = OverheadMapState.Hidden
      }
      break
  }
  return newState
})

const getTransform = (translation, scale) => {
  return `translate(${translation.x}px, ${translation.y}px) scale(${scale})`
}

const highlightAllStops = () => {
  state.stopIndex = -2
  state.animationTimer = setInterval(() => {
    state.stopIndex++
    if (state.stopIndex == 6) {
      state.stopIndex = 0
      stopAnimationTimer()
    }
  }, 750)
}

const stopAnimationTimer = () => {
  clearInterval(state.animationTimer)
  state.animationTimer = 0
  state.animationHasPlayed = true
}

const doMapTransition = () => {
  let newState = currentMapState.value
  let toScale = updateMapScale()
  let fromScale = 1
  let fromTranslation = { x: 0, y: 0 }
  let toTranslation = { x: 0, y: 0 }
  if (newState != state.currentState) {
    if (!props.currentSectionHasLoaded) {
      gsap.to(element.value, { opacity: 0, duration: 0.5 })
    } else {
      switch (newState) {
        case OverheadMapState.Large:
          if (!state.animationHasPlayed && state.animationTimer == 0)
            highlightAllStops()
          toTranslation = getTranslationFromScaleForState(toScale, newState)
          if (state.currentState == OverheadMapState.Small) {
            gsap.to(element.value, {
              transform: getTransform(toTranslation, toScale),
              transformOrigin: 'top left',
              opacity: 1,
              duration: 0.5,
              delay: 1,
              ease: 'power3.inOut',
            })
          } else {
            fromScale = toScale * 0.75
            fromTranslation = getTranslationFromScaleForState(
              fromScale,
              newState
            )
            gsap.fromTo(
              element.value,
              {
                transform: getTransform(fromTranslation, fromScale),
                transformOrigin: 'top left',
                opacity: 0,
              },
              {
                transform: getTransform(toTranslation, toScale),
                transformOrigin: 'top left',
                opacity: 1,
                duration: 0.5,
                delay: 1,
                ease: 'power3.inOut',
              }
            )
          }
          break

        case OverheadMapState.Small:
          toScale *= 0.31914 // relative size of small map compared to full size
          toTranslation = getTranslationFromScaleForState(toScale, newState)
          gsap.to(element.value, {
            transform: getTransform(toTranslation, toScale),
            transformOrigin: 'top left',
            opacity: 1,
            duration: 0.5,
            ease: 'power3.inOut',
          })
          break

        case OverheadMapState.Hidden:
          gsap.to(element.value, { opacity: 0, duration: 0.5 })
          break
      }
      state.currentState = newState
    }
  }
}

const updateMapTransform = () => {
  let toScale = updateMapScale()
  let toTranslation = {}
  switch (state.currentState) {
    case OverheadMapState.Large:
      toTranslation = getTranslationFromScaleForState(
        toScale,
        state.currentState
      )
      gsap.to(element.value, {
        transform: getTransform(toTranslation, toScale),
        opacity: 1,
        duration: 0.25,
        transformOrigin: 'top left',
        ease: 'power3.inOut',
      })
      break
    case OverheadMapState.Small:
      toScale *= 0.31914 // relative size of small map compared to full size
      toTranslation = getTranslationFromScaleForState(
        toScale,
        state.currentState
      )
      gsap.to(element.value, {
        transform: getTransform(toTranslation, toScale),
        transformOrigin: 'top left',
        opacity: 1,
        duration: 0.25,
        ease: 'power3.inOut',
      })
      break
  }
}

const handleResize = () => {
  if (resizeTimer) {
    window.clearTimeout(resizeTimer)
  }
  resizeTimer = setTimeout(() => {
    updateMapTransform()
  }, 200)
}

const handleStopClick = (evt) => {
  stopAnimationTimer()
  const currentSectionIndex = useSectionIndex(props.currentSection)
  const index = parseInt(evt.currentTarget.getAttribute('data-index'))
  state.stopIndex = index
  if (
    state.currentState == OverheadMapState.Small &&
    currentSectionIndex != index
  ) {
    sectionChangedBus.emit({
      section: mapData[index].section,
      pageNumber: 0,
      forward: true,
      lastSection: props.currentSection,
    })
  }
}

const restartTour = (evt) => {
  if (props.currentSection != MJPSection.InvitationToExplore) {
    sectionChangedBus.emit({
      section: MJPSection.InvitationToExplore,
      pageNumber: 0,
      forward: false,
      lastSection: props.currentSection,
    })
  }
}

const currentStop = computed(() => {
  let stop = 0
  if (state.currentState == OverheadMapState.Large) {
    stop = state.stopIndex
  } else {
    stop = useSectionToTourIndex(state.activeSection)
  }
  return stop
})

const sectionPanelIsActive = (index) => {
  return (
    props.currentSection == MJPSection.InvitationToExplore &&
    props.currentPage > 0 &&
    state.currentState != OverheadMapState.Small &&
    index == state.stopIndex &&
    !state.animationTimer
  )
}

const sectionPanelClasses = (stop, index) => {
  let classes = ''
  if (window.innerWidth >= mobileBreakpoint) {
    classes = stop.panelPos
  }
  let pointerEventsClass = 'pointer-events-none'
  let opacityClass = 'opacity-0'
  if (sectionPanelIsActive(index)) {
    if (
      state.currentState != OverheadMapState.Hidden &&
      props.currentSectionHasLoaded
    ) {
      pointerEventsClass = 'pointer-events-auto'
    }
    opacityClass = 'opacity-100'
  }
  classes += ' ' + pointerEventsClass + ' ' + opacityClass
  return classes
}

const mapClasses = computed(() => {
  if (props.settingsAreOpen) {
    return 'hidden pointer-events-none'
  } else {
    let classes = ''
    if (state.currentState == OverheadMapState.Small) {
      classes += 'hidden lg:block'
    } else {
      classes += 'block'
    }
    if (state.currentState == OverheadMapState.Hidden) {
      classes += ' pointer-events-none'
    } else {
      classes += ' pointer-events-auto'
    }
    return classes
  }
})

onUpdated(() => {
  doMapTransition()
  if (props.currentSectionHasLoaded) {
    state.activeSection = props.currentSection
  }
})

onUnmounted(() => {
  window.onresize = null
})

return (_ctx: any,_cache: any) => {
  const _directive_resize = _resolveDirective("resize")!

  return _withDirectives((_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", {
      id: "overhead-map",
      ref_key: "element",
      ref: element,
      class: _normalizeClass(["absolute top-0 left-0 opacity-0", _unref(mapClasses)]),
      style: _normalizeStyle({
        width: mapSize.x + 'px',
        height: mapSize.y + 'px',
      })
    }, [
      (state.currentState != _unref(OverheadMapState).Hidden)
        ? (_openBlock(), _createBlock(LakeShape, {
            key: 0,
            class: _normalizeClass(["absolute left-[126px] top-[182px] -mt-10 lg:mt-20", {
          'cursor-pointer': state.currentState == _unref(OverheadMapState).Small,
          'pointer-events-auto': state.currentState == _unref(OverheadMapState).Small,
        }]),
            tabindex: 
          state.currentState == _unref(OverheadMapState).Small
            ? _unref(useTabIndex)(OverheadMapVue, -1, LakeShape, -1)
            : -1
        ,
            "aria-label": _unref(interfaceText).parkOverview,
            onClick: restartTour
          }, null, 8, ["class", "tabindex", "aria-label"]))
        : _createCommentVNode("", true),
      _createVNode(LakePath, {
        class: "absolute left-0 top-[150px] w-[725px] h-[760px] -mt-10 lg:mt-20 pointer-events-none",
        currentStop: state.animationTimer ? 0 : _unref(currentStop)
      }, null, 8, ["currentStop"]),
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(mapData), (stop, index) => {
        return (_openBlock(), _createElementBlock("div", { key: index }, [
          (state.currentState != _unref(OverheadMapState).Hidden)
            ? (_openBlock(), _createBlock(SiteStopButton, {
                key: 0,
                class: _normalizeClass(["absolute -mt-10 lg:mt-20 pointer-events-auto focus-visible:outline focus-visible:outline-8", stop.buttonPos]),
                tabindex: _unref(useTabIndex)(OverheadMapVue, index, SiteStopButton, -1),
                "aria-label": `${_unref(interfaceText).stop} ${index + 1}`,
                text: (index + 1).toString(),
                viewSize: state.currentState,
                highlighted: index <= _unref(currentStop),
                "data-index": index,
                onClick: handleStopClick
              }, null, 8, ["class", "tabindex", "aria-label", "text", "viewSize", "highlighted", "data-index"]))
            : _createCommentVNode("", true),
          (state.currentState == _unref(OverheadMapState).Large)
            ? (_openBlock(), _createElementBlock("span", _hoisted_2, [
                (_openBlock(), _createBlock(SectionPanel, {
                  key: index,
                  index: index,
                  class: _normalizeClass(["desktop-section-panel absolute hidden lg:block lg:mt-20", sectionPanelClasses(stop, index)]),
                  title: _unref(mapText).stops[index].title,
                  text: _unref(mapText).stops[index].body,
                  remedyCategories: stop.remedyCategories,
                  currentSection: props.currentSection,
                  section: stop.section,
                  isActive: 
              state.currentState == _unref(OverheadMapState).Large &&
              index == state.stopIndex
            
                }, null, 8, ["index", "class", "title", "text", "remedyCategories", "currentSection", "section", "isActive"]))
              ]))
            : _createCommentVNode("", true)
        ]))
      }), 128))
    ], 6),
    (state.currentState == _unref(OverheadMapState).Large)
      ? (_openBlock(), _createElementBlock("span", _hoisted_3, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(mapData), (stop, index) => {
            return (_openBlock(), _createBlock(SectionPanel, {
              key: index,
              index: index,
              class: _normalizeClass(["mobile-section-panel absolute bottom-20 sm:bottom-[110px] left-0 sm:left-7 right-0 sm:right-7 mx-6 lg:hidden", sectionPanelClasses(stop, index)]),
              size: "large",
              title: _unref(mapText).stops[index].title,
              text: _unref(mapText).stops[index].body,
              remedyCategories: stop.remedyCategories,
              currentSection: props.currentSection,
              section: stop.section,
              isActive: 
          state.currentState == _unref(OverheadMapState).Large &&
          index == state.stopIndex
        
            }, null, 8, ["index", "class", "title", "text", "remedyCategories", "currentSection", "section", "isActive"]))
          }), 128))
        ]))
      : _createCommentVNode("", true),
    (state.currentState != _unref(OverheadMapState).Hidden)
      ? (_openBlock(), _createBlock(SiteStopButton, {
          key: 1,
          class: _normalizeClass(["absolute pointer-events-auto lg:hidden top-5 left-1/2 origin-top-left scale-[33%] -ml-[15px]", {
        'opacity-100':
          _unref(useUserIsOnTour)(props.currentSection) &&
          props.currentSectionHasLoaded,
        'opacity-0':
          !_unref(useUserIsOnTour)(props.currentSection) ||
          !props.currentSectionHasLoaded,
      }]),
          "aria-label": _unref(interfaceText).parkOverview,
          text: (Math.max(0, _unref(currentStop)) + 1).toString(),
          viewSize: "Small",
          highlighted: true,
          onClick: restartTour
        }, null, 8, ["class", "aria-label", "text"]))
      : _createCommentVNode("", true)
  ])), [
    [_directive_resize, handleResize]
  ])
}
}

})